import { Injectable } from '@angular/core';

import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { BetslipUpsellConfig } from '@frontend/sports/common/client-config-data-access';
import { CompetitionLogosService } from '@frontend/sports/competition-logos/feature';
import { UserService } from '@frontend/sports/user/feature';
import { Store } from '@ngrx/store';
import { ArcRestrictionService } from 'packages/sports/web/app/src/arc/arc-restriction.service';
import { Observable, combineLatest, map } from 'rxjs';

import { betslipSelector } from '../../../base/store/selectors';
import { IBetslipRootState, IBetslipState } from '../../../base/store/state';
import { IComboBetState } from '../../combo-bet/state';
import { getSelectedToken } from '../../reward-tokens/services/linear-reward-tokens.utils';
import { isAccaBoostToken } from '../../reward-tokens/services/reward-tokens.utils';
import { TokensRecord } from '../../reward-tokens/state';
import { PickGridGroupSport } from '../models';
import { UpsellBetTypeService } from './upsell-bet-type.service';
import { UpsellDataService } from './upsell-data.service';
import { UpsellSettingsService } from './upsell-settings.service';

@Injectable()
export class UpsellVisibilityService {
    constructor(
        private betslipUpsellConfig: BetslipUpsellConfig,
        private userService: UserService,
        private arcRestrictionService: ArcRestrictionService,
        private competitionLogosService: CompetitionLogosService,
        private upsellService: UpsellDataService,
        private upsellSettingsService: UpsellSettingsService,
        private store: Store<IBetslipRootState>,
        private upsellBetTypeService: UpsellBetTypeService,
    ) {}

    isUpsellVisible$(): Observable<boolean> {
        return combineLatest([
            this.store.select(betslipSelector),
            this.upsellSettingsService.upsellVisibility$,
            this.arcRestrictionService.preBetslipMessageEnabled$,
        ]).pipe(
            map(([state, isBetslipUpsellVisible, hideUpsellByArc]) => {
                if (!isBetslipUpsellVisible || hideUpsellByArc) {
                    return false;
                }

                const picks = this.upsellService.getPickDetails(state) || [];
                if (!picks.length) {
                    return false;
                }

                const isUpsellVisible =
                    this.showBetslipUpsell(picks, state) &&
                    (this.upsellBetTypeService.showUpsellForSingle(picks.length, state) ||
                        this.upsellBetTypeService.showUpsellForMulti(picks.length, state));

                return isUpsellVisible;
            }),
        );
    }

    isParticipantImageAvailable(event: EventModel): boolean {
        const isLogoAvailable =
            event.participants.every((participant) => participant.image?.logo) &&
            this.competitionLogosService.hasLogo(event.sport.id, event.league.id);

        const isJerseyAvailable = event.participants.every((participant) => participant.image?.jersey && !participant.image?.logo);

        return isLogoAvailable || isJerseyAvailable;
    }

    private showBetslipUpsell(picks: PickGridGroupSport[], state: IBetslipState): boolean {
        return (
            !this.isBlacklistedCountry() &&
            this.checkPicksOfSameMarket(picks) &&
            this.checkSupportedMarketSport(picks[0]) &&
            this.isEligibleAgainstTokenRewards(state.rewardTokens.tokens, state.types.comboBet)
        );
    }

    private isBlacklistedCountry(): boolean {
        const userCountry = this.userService.isAuthenticated ? this.userService.country : this.userService.geoCountry;
        if (!userCountry) {
            return false;
        }

        return this.betslipUpsellConfig.blackListedCountries?.some((currentCountry) => currentCountry.toUpperCase() === userCountry.toUpperCase());
    }

    private checkPicksOfSameMarket(picks: PickGridGroupSport[]): boolean {
        return picks.every((pick) => pick.sportId === picks[0].sportId && pick.gridGroupId === picks[0].gridGroupId);
    }

    private checkSupportedMarketSport(pick: PickGridGroupSport): boolean {
        return !!this.betslipUpsellConfig.marketGridGroupIds.find((x) => x.gridGroupId === pick.gridGroupId && x.sportId === pick.sportId);
    }

    private isEligibleAgainstTokenRewards(tokens: TokensRecord, comboBetState: IComboBetState): boolean {
        if (!this.userService.isAuthenticated) {
            return true;
        }
        const rewardToken = getSelectedToken(tokens, comboBetState);

        return rewardToken === null || isAccaBoostToken(rewardToken);
    }
}
